/* ═══════════════════════════════════════════════════════════════════════
   "Have a read, and tell me it's right."

   The step between the last question and the declaration, and the only
   place in the product where the customer is asked to check crAIg's work
   rather than answer him.

   ── Why it exists ──────────────────────────────────────────────────────
   Extraction is accurate and it is not perfect. It cannot be: these are
   sampled models, a rare error is still an error, and paying for a bigger
   one moves the rate without ever reaching zero. Deterministic checks
   catch the impossible — a postcode with letters in it — but not the
   plausible-and-wrong, which is the dangerous kind. A job at number 12
   when they said 21 passes every check we can write.

   The person who can always tell is the one it is about.

   ── Why it is not a checkbox on the declaration ────────────────────────
   Because then nobody reads it. Everything is laid out in full, in their
   own words, before the confirm control exists — and anything crAIg was
   unsure about is pulled to the top under its own heading rather than
   left for them to notice halfway down.

   Confirming something that was flagged is allowed and recorded. People
   do have one-word names and odd addresses, and the reviewer wants the
   difference between "they were asked and said it was right" and "nobody
   noticed".

   ── What this is NOT ───────────────────────────────────────────────────
   It is not a transfer of responsibility. Whoever the case goes to still
   has to check it — for a regulated practice that duty is not theirs to
   hand to a customer. What it produces is a declaration signed over data
   the client demonstrably saw, which is worth a great deal more than one
   signed over data they never looked at.
   ═══════════════════════════════════════════════════════════════════════ */

import { useState } from "react";
import type { CaseView, PackView } from "../api";
import { api } from "../api";
import { SectionLabel } from "./chrome";

export function ReviewSheet({
  kase,
  pack,
  onReviewed,
  onCorrect,
}: {
  kase: CaseView;
  pack: PackView;
  onReviewed(c: CaseView): void;
  /** Jumps to the file drawer row so they can fix it themselves. */
  onCorrect(ref: string): void;
}) {
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      onReviewed((await api.review(kase.id, kase.flags.map((f) => f.ref))).case);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  /* Only what they actually told us. A row reading "not provided" invites
     them to answer it here, and this screen has no way to take an answer —
     anything missing is crAIg's job, and if it were required we would not
     have got this far. */
  const answered = pack.fields
    /* Never an internal value. It is a real answer and it is not one they
       gave — "Starting point: trades-plumbing" is our filing system, on
       the last screen before somebody signs. */
    .filter((f) => !f.internal)
    .map((f) => ({ ...f, value: kase.fields[f.name] ?? "" }))
    .filter((f) => f.value.trim());

  const lists = pack.checklists
    .map((l) => ({
      ...l,
      picked: (kase.checklists[l.id] ?? [])
        .filter((id) => id !== l.noneId)
        .map((id) => l.options.find((o) => o.id === id)?.label ?? id),
      rows: kase.items[l.bucket ?? l.id] ?? [],
    }))
    .filter((l) => l.picked.length || l.rows.length);

  return (
    <div className="card px-5 py-5 space-y-5">
      <div>
        <SectionLabel>Before we finish</SectionLabel>
        <h2 style={{ fontSize: 19, fontWeight: 650, margin: "6px 0 4px", letterSpacing: "-0.01em" }}>
          Have a read and check I've got it right
        </h2>
        <p style={{ fontSize: 14.5, color: "var(--ink-2)", lineHeight: 1.55 }}>
          This is everything I've written down. You know it better than I do — if anything looks
          off, change it here. It only takes a minute and it saves your{" "}
          {pack.copy.reviewerNoun} ringing you about it later.
        </p>
      </div>

      {/* Doubts first, and impossible to scroll past. */}
      {kase.flags.length > 0 && (
        <div
          className="card px-4 py-4"
          style={{ background: "var(--warn-tint)", borderColor: "var(--warn)" }}
        >
          <SectionLabel>Worth a second look</SectionLabel>
          <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: "6px 0 10px" }}>
            These might be right — I just wasn't certain.
          </p>
          <ul className="space-y-2" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {kase.flags.map((f) => (
              <li key={f.ref} className="flex items-start justify-between gap-3">
                <span style={{ fontSize: 14 }}>{f.text}</span>
                <button
                  type="button"
                  className="btn btn-quiet"
                  style={{ padding: "0.25rem 0.6rem", fontSize: 12.5, flexShrink: 0 }}
                  onClick={() => onCorrect(f.ref)}
                >
                  Fix
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {answered.length > 0 && (
          <Group title="Your details">
            {answered.map((f) => (
              <Row key={f.name} label={f.label} value={f.value} onFix={() => onCorrect(f.name)} />
            ))}
          </Group>
        )}

        {lists.map((l) => (
          <Group key={l.id} title={l.title}>
            {l.rows.length > 0
              ? l.rows.map((r, i) => (
                  <Row
                    key={`${r.sourceId}-${i}`}
                    label={r.what}
                    value={[r.amount ? `$${r.amount}` : "", r.when ?? ""].filter(Boolean).join(" · ")}
                    note={r.detail}
                    onFix={() => onCorrect(r.sourceId)}
                  />
                ))
              : l.picked.map((p) => <Row key={p} label={p} value="" />)}
          </Group>
        ))}

        {kase.documents.length > 0 && (
          <Group title="What you sent">
            <Row label={`${kase.documents.length} file${kase.documents.length === 1 ? "" : "s"}`} value="" />
          </Group>
        )}
      </div>

      {error && (
        <p role="alert" style={{ fontSize: 13.5, color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {/* The control appears under everything, not above it. */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 16 }}>
        <label className="flex items-start gap-2.5" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={ticked}
            onChange={(e) => setTicked(e.target.checked)}
            style={{ marginTop: 3, width: 17, height: 17, flexShrink: 0 }}
          />
          <span style={{ fontSize: 14.5, lineHeight: 1.5 }}>
            I've checked everything on this page and it's all correct.
          </span>
        </label>

        <button
          type="button"
          className="btn btn-primary w-full"
          disabled={!ticked || busy}
          onClick={confirm}
          style={{ marginTop: 14 }}
        >
          {busy ? "One moment…" : "Yes, that's all correct"}
        </button>
      </div>
    </div>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      <div className="mt-2 space-y-1.5">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  note,
  onFix,
}: {
  label: string;
  value: string;
  note?: string;
  onFix?: () => void;
}) {
  return (
    <div
      className="flex items-start justify-between gap-3 px-3 py-2"
      style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius)" }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{label}</div>
        {value && <div style={{ fontSize: 14.5, fontWeight: 550 }}>{value}</div>}
        {note && (
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2, lineHeight: 1.45 }}>
            {note}
          </div>
        )}
      </div>
      {onFix && (
        <button
          type="button"
          className="btn btn-quiet"
          style={{ padding: "0.2rem 0.55rem", fontSize: 12, flexShrink: 0 }}
          onClick={onFix}
        >
          Change
        </button>
      )}
    </div>
  );
}
